import { ProductRedeemFee } from 'src/type/gold-withdraw';
import Decimal from 'decimal.js';

export type AmountBreakdownItem = {
  config: ProductRedeemFee;
  count: number;
  totalAmount: number;
};

export type AmountBreakdownResult = {
  isValid: boolean;
  breakdown: AmountBreakdownItem[];
  remainingAmount: number;
  totalMatchedAmount: number;
};

/**
 * หาการแบ่งค่าที่กรอกมาให้ตรงกับ product redeem fee
 * โดยจะเริ่มจากค่าใหญ่สุดในหน่วยเดียวกันก่อน
 *
 * @param inputAmount - ค่าที่กรอกมา
 * @param unitType - หน่วย ('BAHT' หรือ 'GRAM')
 * @param productRedeemFees - รายการ product redeem fee
 * @returns ผลลัพธ์การแบ่งค่า
 */
export function calculateAmountBreakdown(
  inputAmount: number,
  unitType: 'BAHT' | 'GRAM',
  productRedeemFees: ProductRedeemFee[]
): AmountBreakdownResult {
  if (inputAmount <= 0 || productRedeemFees.length === 0) {
    return {
      isValid: false,
      breakdown: [],
      remainingAmount: inputAmount,
      totalMatchedAmount: 0,
    };
  }

  // กรอง config ที่มีค่าในหน่วยที่ต้องการเท่านั้น และเตรียม amount ตามหน่วย
  const relevantConfigs = productRedeemFees
    .filter((config) => {
      const amount = unitType === 'BAHT' ? config.weightBaht : config.weightGram;
      return amount > 0;
    })
    .map((config) => ({
      ...config,
      amount: unitType === 'BAHT' ? config.weightBaht : config.weightGram,
    }))
    .sort((a, b) => b.amount - a.amount);

  if (relevantConfigs.length === 0) {
    return {
      isValid: false,
      breakdown: [],
      remainingAmount: inputAmount,
      totalMatchedAmount: 0,
    };
  }

  // ใช้วิธี optimal combination (unbounded knapsack) เพื่อหาผลรวมที่ใกล้ input มากที่สุดและเศษน้อยสุด
  // 1) แปลงเป็นจำนวนเต็มโดยอิงจากทศนิยมสูงสุดของ config และ input
  const getDecimals = (num: number): number => {
    const s = String(num);
    if (!s.includes('.')) return 0;
    return s.split('.')[1].length;
  };

  const maxDecimals = Math.max(
    getDecimals(inputAmount),
    ...relevantConfigs.map((c) => getDecimals(c.amount))
  );

  // จำกัดทศนิยมไม่เกิน 4 (ตามระบบฟอร์ม config)
  const scalePow = Math.min(4, Math.max(0, maxDecimals));
  const scale = 10 ** scalePow;

  const toInt = (num: number): number =>
    new Decimal(num).times(scale).toDP(0, Decimal.ROUND_HALF_UP).toNumber();

  let target = toInt(inputAmount);
  let coins = relevantConfigs.map((c) => toInt(c.amount));

  // 2) ลดสเกลด้วย GCD เพื่อลดความซับซ้อนของ DP
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const gcdAll = coins.reduce((acc, v) => gcd(acc, v), coins[0] || 1);
  const g = gcd(gcdAll || 1, target || 0) || 1;

  target = Math.floor(target / g);
  coins = coins.map((v) => Math.floor(v / g));

  // ป้องกันเคส coin ใดๆ เป็น 0 หลังหารด้วย g
  coins = coins.filter((v) => v > 0);

  if (coins.length === 0) {
    return {
      isValid: false,
      breakdown: [],
      remainingAmount: inputAmount,
      totalMatchedAmount: 0,
    };
  }

  // 3) DP แบบ unbounded เพื่อหาค่าที่รวมได้สูงสุดไม่เกิน target
  const dp: boolean[] = new Array(target + 1).fill(false);
  const prev: number[] = new Array(target + 1).fill(-1); // เก็บ index ของเหรียญที่ใช้ล่าสุด

  dp[0] = true;
  for (let a = 1; a <= target; a += 1) {
    for (let i = 0; i < coins.length; i += 1) {
      const c = coins[i];
      if (a - c >= 0 && dp[a - c]) {
        dp[a] = true;
        prev[a] = i;
        break; // เจอวิธีแรกก็พอ เพื่อ reconstruction
      }
    }
  }

  // 4) หา a ที่ดีที่สุด (ถ้า exact match ได้จะเป็น target เลย)
  let best = -1;
  for (let a = target; a >= 0; a -= 1) {
    if (dp[a]) {
      best = a;
      break;
    }
  }

  if (best < 0) {
    return {
      isValid: false,
      breakdown: [],
      remainingAmount: inputAmount,
      totalMatchedAmount: 0,
    };
  }

  // 5) สร้าง breakdown จาก prev โดยนับจำนวนเหรียญแต่ละชนิด
  const countByCoinIndex: Record<number, number> = {};
  let cur = best;
  while (cur > 0 && prev[cur] !== -1) {
    const idx = prev[cur];
    countByCoinIndex[idx] = (countByCoinIndex[idx] || 0) + 1;
    cur -= coins[idx];
  }

  // 6) map กลับเป็น breakdown ตาม relevantConfigs (เรียงตาม amount เดิมจากมากไปน้อย)
  const breakdown: AmountBreakdownItem[] = [];
  let totalMatchedDecimal = new Decimal(0);

  // เราต้องอ้างอิงจำนวนครั้งต่อ amount เดิมก่อนหารด้วย g และ scale
  const coinAmountToConfigIndex: Record<number, number[]> = {};
  relevantConfigs.forEach((conf, idx) => {
    const scaled = Math.floor(toInt(conf.amount) / g);
    if (scaled > 0) {
      if (!coinAmountToConfigIndex[scaled]) coinAmountToConfigIndex[scaled] = [];
      coinAmountToConfigIndex[scaled].push(idx);
    }
  });

  Object.keys(countByCoinIndex)
    .map((k) => Number(k))
    .sort((a, b) => coins[b] - coins[a])
    .forEach((coinIdx) => {
      const usedCount = countByCoinIndex[coinIdx];
      const coinAmountReduced = coins[coinIdx];

      // เลือก config แรกที่ match amount นี้
      const configIndices = coinAmountToConfigIndex[coinAmountReduced] || [];
      if (configIndices.length === 0) return;
      const conf = relevantConfigs[configIndices[0]];

      const singleAmount = conf.amount; // จำนวนจริงในหน่วยเดิม
      const totalAmount = new Decimal(singleAmount).times(usedCount).toNumber();
      breakdown.push({
        config: conf,
        count: usedCount,
        totalAmount,
      });
      totalMatchedDecimal = totalMatchedDecimal.plus(totalAmount);
    });

  // 7) คำนวณเศษที่เหลือในหน่วยจริง
  const totalMatchedAmount = totalMatchedDecimal.toNumber();
  const remainingAmount = Decimal.max(
    new Decimal(inputAmount).minus(totalMatchedAmount),
    new Decimal(0)
  ).toNumber();
  const isValid = new Decimal(remainingAmount).abs().lt(0.0001); // เหลือประมาณ 0 ถือว่าแบ่งบล็อคได้

  return {
    isValid,
    breakdown,
    remainingAmount,
    totalMatchedAmount,
  };
}

/**
 * แปลงผลลัพธ์การแบ่งค่าเป็นข้อความที่อ่านง่าย
 *
 * @param result - ผลลัพธ์จาก calculateAmountBreakdown
 * @param unitType - หน่วย ('BAHT' หรือ 'GRAM')
 * @returns ข้อความที่อธิบายการแบ่งค่า
 */
export function formatBreakdownResult(
  result: AmountBreakdownResult,
  unitType: 'BAHT' | 'GRAM'
): string {
  if (!result.isValid) {
    return `ไม่สามารถแบ่งค่าได้`;
  }

  if (result.breakdown.length === 0) {
    return 'ไม่พบการแบ่งค่าที่เหมาะสม';
  }

  const unitText = unitType === 'BAHT' ? 'บาท' : 'กรัม';
  const breakdownText = result.breakdown
    .map((item) => `${item.count} × ${item.config.name}`)
    .join(' + ');

  return `${breakdownText} = ${result.totalMatchedAmount} ${unitText}`;
}

/**
 * ตัวอย่างการใช้งานตามที่ผู้ใช้ต้องการ
 */
// exampleUsage ถูกลบออกในการใช้งาน production
